import { useState } from 'react';
import type { TermListItem, CreateTermRequest } from '@acitra/shared';
import { createTerm } from '../../services/api';

interface CreateTermModalProps {
  tenantId: string;
  isOpen: boolean;
  onClose: () => void;
  onTermCreated: (term: TermListItem) => void;
  /** Pre-fill values from extracted payment terms */
  initialValues?: {
    dueDays?: number;
    discountPercent?: number;
    discountDays?: number;
  };
}

export function CreateTermModal({
  tenantId,
  isOpen,
  onClose,
  onTermCreated,
  initialValues,
}: CreateTermModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<'STANDARD' | 'DATE_DRIVEN'>('STANDARD');
  const [dueDays, setDueDays] = useState<number | ''>(initialValues?.dueDays ?? '');
  const [discountPercent, setDiscountPercent] = useState<number | ''>(
    initialValues?.discountPercent ?? ''
  );
  const [discountDays, setDiscountDays] = useState<number | ''>(initialValues?.discountDays ?? '');
  const [dayOfMonthDue, setDayOfMonthDue] = useState<number | ''>('');
  const [discountDayOfMonth, setDiscountDayOfMonth] = useState<number | ''>('');
  const [dueNextMonthDays, setDueNextMonthDays] = useState<number | ''>('');

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Generate suggested name based on values
  const generateName = () => {
    if (type === 'STANDARD') {
      if (discountPercent && discountDays && dueDays) {
        return `${discountPercent}/${discountDays} Net ${dueDays}`;
      }
      if (dueDays) {
        return `Net ${dueDays}`;
      }
    } else {
      if (dayOfMonthDue) {
        return `Due ${dayOfMonthDue}th of Month`;
      }
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const request: CreateTermRequest = {
        name: name.trim(),
        type,
      };

      if (type === 'STANDARD') {
        if (dueDays !== '') request.dueDays = dueDays;
        if (discountPercent !== '') request.discountPercent = discountPercent;
        if (discountDays !== '') request.discountDays = discountDays;
      } else {
        if (dayOfMonthDue !== '') request.dayOfMonthDue = dayOfMonthDue;
        if (discountDayOfMonth !== '') request.discountDayOfMonth = discountDayOfMonth;
        if (dueNextMonthDays !== '') request.dueNextMonthDays = dueNextMonthDays;
      }

      const term = await createTerm(tenantId, request);
      onTermCreated(term);
      handleClose();
    } catch (err) {
      console.error('Failed to create term:', err);
      setError(err instanceof Error ? err.message : 'Failed to create payment term');
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setName('');
    setType('STANDARD');
    setDueDays(initialValues?.dueDays ?? '');
    setDiscountPercent(initialValues?.discountPercent ?? '');
    setDiscountDays(initialValues?.discountDays ?? '');
    setDayOfMonthDue('');
    setDiscountDayOfMonth('');
    setDueNextMonthDays('');
    setError(null);
    onClose();
  };

  const handleSuggestName = () => {
    const suggested = generateName();
    if (suggested) {
      setName(suggested);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={handleClose} />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Add Payment Term</h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
            {/* Name */}
            <div>
              <label htmlFor="term-name" className="block text-sm font-medium text-gray-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="term-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                  placeholder="e.g., Net 30, 2/10 Net 30"
                  required
                />
                <button
                  type="button"
                  onClick={handleSuggestName}
                  className="px-3 py-2 text-sm text-primary-600 hover:text-primary-700 border border-gray-300 rounded-md hover:bg-gray-50"
                  title="Generate name from values"
                >
                  Auto
                </button>
              </div>
            </div>

            {/* Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <div className="flex gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="STANDARD"
                    checked={type === 'STANDARD'}
                    onChange={() => setType('STANDARD')}
                    className="form-radio text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Standard (days based)</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="type"
                    value="DATE_DRIVEN"
                    checked={type === 'DATE_DRIVEN'}
                    onChange={() => setType('DATE_DRIVEN')}
                    className="form-radio text-primary-600"
                  />
                  <span className="ml-2 text-sm text-gray-700">Date driven</span>
                </label>
              </div>
            </div>

            {/* Standard type fields */}
            {type === 'STANDARD' && (
              <>
                <div>
                  <label
                    htmlFor="due-days"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Due Days
                  </label>
                  <input
                    type="number"
                    id="due-days"
                    value={dueDays}
                    onChange={(e) =>
                      setDueDays(e.target.value === '' ? '' : parseInt(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="e.g., 30"
                    min="0"
                  />
                  <p className="mt-1 text-xs text-gray-500">Number of days until payment is due</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="discount-percent"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Discount %
                    </label>
                    <input
                      type="number"
                      id="discount-percent"
                      value={discountPercent}
                      onChange={(e) =>
                        setDiscountPercent(e.target.value === '' ? '' : parseFloat(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                      placeholder="e.g., 2"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="discount-days"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Discount Days
                    </label>
                    <input
                      type="number"
                      id="discount-days"
                      value={discountDays}
                      onChange={(e) =>
                        setDiscountDays(e.target.value === '' ? '' : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                      placeholder="e.g., 10"
                      min="0"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Optional early payment discount (e.g., 2% if paid within 10 days)
                </p>
              </>
            )}

            {/* Date driven type fields */}
            {type === 'DATE_DRIVEN' && (
              <>
                <div>
                  <label
                    htmlFor="day-of-month-due"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Day of Month Due
                  </label>
                  <input
                    type="number"
                    id="day-of-month-due"
                    value={dayOfMonthDue}
                    onChange={(e) =>
                      setDayOfMonthDue(e.target.value === '' ? '' : parseInt(e.target.value))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                    placeholder="e.g., 15"
                    min="1"
                    max="31"
                  />
                  <p className="mt-1 text-xs text-gray-500">Day of month when payment is due</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="discount-day-of-month"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Discount Day
                    </label>
                    <input
                      type="number"
                      id="discount-day-of-month"
                      value={discountDayOfMonth}
                      onChange={(e) =>
                        setDiscountDayOfMonth(e.target.value === '' ? '' : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                      placeholder="e.g., 5"
                      min="1"
                      max="31"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="due-next-month-days"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Due Next Month
                    </label>
                    <input
                      type="number"
                      id="due-next-month-days"
                      value={dueNextMonthDays}
                      onChange={(e) =>
                        setDueNextMonthDays(e.target.value === '' ? '' : parseInt(e.target.value))
                      }
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                      placeholder="e.g., 5"
                      min="0"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  Days before due date that pushes payment to next month
                </p>
              </>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={isCreating || !name.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Term'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
